import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  queryKeys,
  RESOURCE_LIST_FETCH_STALE_MS,
} from '@cotera/client/app/hooks/query-cache-keys';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';

const EMPTY_EVENT_NAMES: readonly string[] = [];

type EmitEventEntry = {
  readonly eventName: string;
  readonly payloadFields: readonly string[];
};

const EMPTY_EVENTS: readonly EmitEventEntry[] = [];

/** Shared query for the org's emit-event nodes (names + declared payload fields). */
function useEmitEvents(): readonly EmitEventEntry[] {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const { data } = useQuery({
    queryKey: queryKeys.workflow.events({ orgId }),
    staleTime: RESOURCE_LIST_FETCH_STALE_MS,
    queryFn: async (): Promise<readonly EmitEventEntry[]> => {
      const result = await client.resource.workflow.listEvents();
      if (result.isErr()) {
        return EMPTY_EVENTS;
      }
      return result.value.events;
    },
  });
  return data ?? EMPTY_EVENTS;
}

export function useEmitEventNameSuggestions(): readonly string[] {
  const events = useEmitEvents();
  return useMemo(
    () =>
      events.length === 0
        ? EMPTY_EVENT_NAMES
        : events.map((event) => event.eventName),
    [events]
  );
}

/**
 * Union of payload field names each event carries, keyed by event name. Powers
 * field suggestions for nodes wired downstream of an event trigger.
 */
export function useEmitEventPayloadFieldsByName(): ReadonlyMap<
  string,
  readonly string[]
> {
  const events = useEmitEvents();
  return useMemo(() => {
    const map = new Map<string, readonly string[]>();
    for (const event of events) {
      map.set(event.eventName, event.payloadFields);
    }
    return map;
  }, [events]);
}
